/**
 * Command argument parser for standardized flag and option handling
 * Supports UNIX-style short flags (-a), long flags (--all), and options with values
 */

export interface ParsedArgs {
  /** Short and long flags (boolean values) */
  flags: Set<string>
  /** Options with values (--name=value or --name value) */
  options: Map<string, string>
  /** Positional arguments (non-flag/option arguments) */
  positional: string[]
}

/**
 * Parse command arguments into flags, options, and positional arguments
 *
 * @param args - Array of argument strings
 * @returns Parsed arguments with flags, options, and positional values
 *
 * @example
 * ```typescript
 * // Short flags
 * parseCommandArgs(['-a', '-l'])
 * // => { flags: Set(['a', 'l']), options: Map(), positional: [] }
 *
 * // Combined short flags
 * parseCommandArgs(['-al'])
 * // => { flags: Set(['a', 'l']), options: Map(), positional: [] }
 *
 * // Long flags
 * parseCommandArgs(['--all', '--long'])
 * // => { flags: Set(['all', 'long']), options: Map(), positional: [] }
 *
 * // Options with values
 * parseCommandArgs(['--name=value'])
 * // => { flags: Set(), options: Map([['name', 'value']]), positional: [] }
 *
 * parseCommandArgs(['--name', 'value'])
 * // => { flags: Set(), options: Map([['name', 'value']]), positional: [] }
 *
 * // Positional arguments
 * parseCommandArgs(['file.txt', '-a', 'dir/'])
 * // => { flags: Set(['a']), options: Map(), positional: ['file.txt', 'dir/'] }
 *
 * // Mixed
 * parseCommandArgs(['-al', '--verbose', 'file.txt', '--output', 'result.txt'])
 * // => {
 * //   flags: Set(['a', 'l', 'verbose']),
 * //   options: Map([['output', 'result.txt']]),
 * //   positional: ['file.txt']
 * // }
 * ```
 */
export function parseCommandArgs(args: string[]): ParsedArgs {
  const flags = new Set<string>()
  const options = new Map<string, string>()
  const positional: string[] = []

  for (let i = 0; i < args.length; i++) {
    const arg = args[i]

    // Long option with equals: --name=value
    if (arg.startsWith('--') && arg.includes('=')) {
      const [name, ...valueParts] = arg.slice(2).split('=')
      const value = valueParts.join('=') // Handle values with '=' in them
      options.set(name, value)
      continue
    }

    // Long flag or option: --name
    if (arg.startsWith('--')) {
      const name = arg.slice(2)

      // Check if next arg is a value (not a flag)
      const nextArg = args[i + 1]
      if (nextArg && !nextArg.startsWith('-')) {
        // This is an option with a value: --name value
        options.set(name, nextArg)
        i++ // Skip the next arg since we consumed it
      } else {
        // This is just a flag: --name
        flags.add(name)
      }
      continue
    }

    // Short flags: -a or -abc (combined)
    if (arg.startsWith('-') && arg.length > 1) {
      // Extract each character as a separate flag
      for (const char of arg.slice(1)) {
        flags.add(char)
      }
      continue
    }

    // Positional argument
    positional.push(arg)
  }

  return { flags, options, positional }
}

/**
 * Check if a specific flag is present (supports both short and long names)
 *
 * @param parsed - Parsed arguments
 * @param shortName - Short flag name (e.g., 'a')
 * @param longName - Long flag name (e.g., 'all')
 * @returns true if either flag is present
 *
 * @example
 * ```typescript
 * const parsed = parseCommandArgs(['-a'])
 * hasFlag(parsed, 'a', 'all') // => true
 *
 * const parsed2 = parseCommandArgs(['--all'])
 * hasFlag(parsed2, 'a', 'all') // => true
 * ```
 */
export function hasFlag(
  parsed: ParsedArgs,
  shortName: string,
  longName?: string
): boolean {
  return (
    parsed.flags.has(shortName) || (longName ? parsed.flags.has(longName) : false)
  )
}

/**
 * Get an option value with fallback to default
 *
 * @param parsed - Parsed arguments
 * @param name - Option name
 * @param defaultValue - Default value if option not present
 * @returns Option value or default
 *
 * @example
 * ```typescript
 * const parsed = parseCommandArgs(['--output', 'file.txt'])
 * getOption(parsed, 'output', 'default.txt') // => 'file.txt'
 *
 * const parsed2 = parseCommandArgs([])
 * getOption(parsed2, 'output', 'default.txt') // => 'default.txt'
 * ```
 */
export function getOption(
  parsed: ParsedArgs,
  name: string,
  defaultValue: string = ''
): string {
  return parsed.options.get(name) || defaultValue
}
